import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import {
  useListClientUsersQuery,
  useSearchClientDocumentsQuery,
  useGetCrmSummaryQuery,
} from '../../app/api';
import { useIsMobile } from '../utils';
import useRoleJSON from './useRoleJSON';

// The Work side reuses the brand palette but applies it more sparingly:
// hairline borders instead of soft shadows, tighter corner radii, a cool
// neutral canvas (vs the warmer cream `softGray` of the Hub), and goldenrod
// reserved for accents rather than headers. The goal is a quieter,
// institutional feel — closer to a wealth-management workstation than the
// Hub's friendlier company-portal vibe.
const work = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  green:     '#1a7f4e',
  ink:       '#0a2947',
  muted:     '#6b7280',
  hairline:  '#dde2e8',
  hairlineStrong: '#c4cbd4',
  surface:   '#ffffff',
  canvas:    '#f3f5f8',
};

// Pages still being built, shown to BETA holders only. Same gates as the Beta
// group in Root.jsx's workMenu — keep the two in step.
const BETA_PAGES = [
  { icon: 'people-fill',    label: 'Salesforce Clients', sub: 'The FSC book of business',        path: '/work/clients/fsc' },
  { icon: 'graph-up-arrow', label: 'Net New AUM',        sub: 'Flows by firm and advisor',       path: '/work/reports/aum' },
  { icon: 'calendar-week',  label: 'CRM Calendar',       sub: "A firm's office calendar",        path: '/work/crm/calendar', workRole: 'isWorkCrm' },
  { icon: 'stars',          label: 'Agentic WM',         sub: 'Per-client advisor conversations', path: '/work/agentic-wm',   workRole: 'isWorkCrm' },
  { icon: 'check2-square',  label: 'Tasks',              sub: 'Client service tasks and queues', path: '/work/tasks',        workRole: 'isWorkTasks' },
];

const DAY_MS = 24 * 60 * 60 * 1000;
const fmtNum = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString());
const fmtDay = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '');
const fullName = (first, last, fallback) => `${first || ''} ${last || ''}`.trim() || fallback || '—';

/**
 * Work → Home. The landing page for the side: one panel per Work area the
 * person holds (WORK_PORTAL_CLIENTS / WORK_DOCUMENTS / WORK_CRM), each with a
 * few counts and the latest items, plus the beta pages for BETA holders.
 *
 * Each panel is its own component so its query only runs when it is rendered —
 * someone without an area never calls that area's API (which would 403).
 * Counts come from the same endpoints the area pages use, so they are office-
 * scoped exactly like those pages.
 */
export default function WorkHome() {
  const isMobile = useIsMobile();
  const roleJSON = useRoleJSON();

  const areas = roleJSON ? {
    portal:    Boolean(roleJSON.isWorkPortalClients),
    documents: Boolean(roleJSON.isWorkDocuments),
    crm:       Boolean(roleJSON.isWorkCrm),
  } : null;
  const betaPages = roleJSON?.isBeta
    ? BETA_PAGES.filter(p => !p.workRole || roleJSON[p.workRole])
    : [];
  const hasAnyArea = areas && (areas.portal || areas.documents || areas.crm);

  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div style={{ background: work.canvas, minHeight: '100vh', padding: isMobile ? '1rem' : '1.5rem 1.75rem', color: work.ink }}>

      {/* Header — flat panel with a thin navy accent on top instead of the
          Hub's full deep-blue banner. Sets the visual tone for the side. */}
      <div style={{
        background: work.surface,
        border: `1px solid ${work.hairline}`,
        borderTop: `3px solid ${work.deepBlue}`,
        borderRadius: 6,
        padding: isMobile ? '1.1rem 1.1rem' : '1.4rem 1.6rem',
        marginBottom: '1.25rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', flexWrap: 'wrap' }}>
          <div style={{
            background: work.deepBlue,
            color: 'white',
            width: 44, height: 44,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 6,
            flexShrink: 0,
          }}>
            <i className="bi bi-briefcase-fill" style={{ fontSize: '1.3rem' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ color: work.muted, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', margin: '0 0 2px' }}>
              {today}
            </p>
            <h3 style={{ color: work.ink, fontWeight: 800, margin: 0, fontSize: isMobile ? '1.2rem' : '1.4rem', lineHeight: 1.15 }}>
              Work Home
            </h3>
          </div>
          {areas?.portal && (
            <Link to="/work/clients/new" className="btn btn-sm"
                  style={{ background: work.deepBlue, color: 'white', fontWeight: 600, borderRadius: 4 }}>
              <i className="bi bi-person-plus-fill me-1" />New portal client
            </Link>
          )}
        </div>
      </div>

      {!roleJSON && (
        <div style={{ textAlign: 'center', padding: '3rem 0' }}><Spinner animation="border" size="sm" /></div>
      )}

      {roleJSON && !hasAnyArea && (
        <div style={{
          background: work.surface, border: `1px solid ${work.hairline}`, borderRadius: 6,
          padding: '2rem 1.5rem', textAlign: 'center', color: work.muted, marginBottom: '1.25rem',
        }}>
          <i className="bi bi-lock" style={{ fontSize: '1.6rem', color: work.hairlineStrong }} />
          <div style={{ fontWeight: 700, color: work.ink, marginTop: '0.5rem' }}>No Work areas yet</div>
          <div style={{ fontSize: '0.88rem', marginTop: 4 }}>
            Ask a Super Admin to give you access to Portal Clients, Documents or CRM.
          </div>
        </div>
      )}

      {hasAnyArea && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '1rem',
          marginBottom: '1.25rem',
          alignItems: 'start',
        }}>
          {areas.portal && <PortalClientsPanel />}
          {areas.documents && <DocumentsPanel />}
          {areas.crm && <CrmPanel />}
        </div>
      )}

      {betaPages.length > 0 && <BetaPanel pages={betaPages} isMobile={isMobile} />}
    </div>
  );
}

function Panel({ icon, title, to, children }) {
  return (
    <div style={{ background: work.surface, border: `1px solid ${work.hairline}`, borderRadius: 6, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.8rem 1rem', borderBottom: `1px solid ${work.hairline}` }}>
        <div style={{
          width: 32, height: 32, borderRadius: 6,
          background: 'rgba(10,41,71,0.06)', color: work.deepBlue,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <i className={`bi bi-${icon}`} />
        </div>
        <div style={{ fontWeight: 700, fontSize: '1rem', flex: 1 }}>{title}</div>
        <Link to={to} style={{ color: work.tealBlue, fontSize: '0.82rem', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}>
          Open <i className="bi bi-chevron-right" style={{ fontSize: '0.7rem' }} />
        </Link>
      </div>
      <div style={{ padding: '0.9rem 1rem 1rem' }}>{children}</div>
    </div>
  );
}
Panel.propTypes = { icon: PropTypes.string.isRequired, title: PropTypes.string.isRequired, to: PropTypes.string.isRequired, children: PropTypes.node };

function Stats({ items }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))`, gap: '0.5rem', marginBottom: '0.9rem' }}>
      {items.map(s => (
        <div key={s.label} style={{ border: `1px solid ${work.hairline}`, borderRadius: 4, padding: '0.5rem 0.6rem' }}>
          <div style={{ fontSize: '1.25rem', fontWeight: 800, color: s.color || work.ink, lineHeight: 1.1 }}>{fmtNum(s.value)}</div>
          <div style={{ fontSize: '0.68rem', fontWeight: 700, color: work.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 2 }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}
Stats.propTypes = { items: PropTypes.arrayOf(PropTypes.object).isRequired };

function ListHeading({ children }) {
  return (
    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: work.muted, textTransform: 'uppercase', letterSpacing: '0.8px', margin: '0 0 0.35rem' }}>
      {children}
    </div>
  );
}
ListHeading.propTypes = { children: PropTypes.node };

function Row({ to, primary, secondary, right }) {
  const inner = (
    <>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '0.88rem', color: to ? work.tealBlue : work.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{primary}</div>
        {secondary && <div style={{ fontSize: '0.76rem', color: work.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{secondary}</div>}
      </div>
      {right && <div style={{ fontSize: '0.74rem', color: work.muted, flexShrink: 0 }}>{right}</div>}
    </>
  );
  const style = { display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.4rem 0', borderTop: `1px solid ${work.hairline}`, textDecoration: 'none' };
  return to ? <Link to={to} style={style}>{inner}</Link> : <div style={style}>{inner}</div>;
}
Row.propTypes = { to: PropTypes.string, primary: PropTypes.node, secondary: PropTypes.node, right: PropTypes.node };

function PanelState({ isLoading, isError, empty, children }) {
  if (isLoading) return <div style={{ textAlign: 'center', padding: '1rem 0' }}><Spinner animation="border" size="sm" /></div>;
  if (isError) return <div style={{ color: work.muted, fontSize: '0.85rem' }}>Could not load this right now.</div>;
  if (empty) return <div style={{ color: work.muted, fontSize: '0.85rem' }}>{empty}</div>;
  return children;
}
PanelState.propTypes = { isLoading: PropTypes.bool, isError: PropTypes.bool, empty: PropTypes.node, children: PropTypes.node };

// Households, grouped the same way the Portal Clients page groups them, so the
// numbers here match the rows there.
function PortalClientsPanel() {
  const { data: clients = [], isLoading, isError } = useListClientUsersQuery();

  const { stats, recent } = useMemo(() => {
    const map = new Map();
    for (const c of clients) {
      const key = c.household_id ? `h${c.household_id}` : `s${c.id}`;
      if (!map.has(key)) map.set(key, { label: c.household_label || null, members: [] });
      map.get(key).members.push(c);
    }
    const groups = Array.from(map.values())
      .filter(g => g.members.some(m => !m.deactivated_at))
      .map(g => {
        const primary = g.members.find(m => m.association === 'primary') || g.members.find(m => m.email) || g.members[0];
        const created = g.members.reduce((max, m) => (m.created && m.created > max ? m.created : max), '');
        return {
          primary,
          created,
          isActive: g.members.some(m => m.is_active && !m.deactivated_at),
          name: g.label || (primary?.last_name ? `${primary.last_name} Household` : fullName(primary?.first_name, primary?.last_name, primary?.email)),
        };
      });
    const active = groups.filter(g => g.isActive).length;
    return {
      stats: [
        { label: 'Households', value: groups.length },
        { label: 'Active', value: active, color: work.green },
        { label: 'In progress', value: groups.length - active, color: work.goldenrod },
      ],
      recent: [...groups].sort((a, b) => (b.created || '').localeCompare(a.created || '')).slice(0, 5),
    };
  }, [clients]);

  return (
    <Panel icon="person-plus-fill" title="Portal Clients" to="/work/clients">
      <PanelState isLoading={isLoading} isError={isError} empty={!clients.length && 'No portal clients in your offices yet.'}>
        <Stats items={stats} />
        <ListHeading>Recently added</ListHeading>
        {recent.map(g => (
          <Row
            key={g.primary.public_id}
            to={`/work/clients/${g.primary.public_id}`}
            primary={g.name}
            secondary={g.isActive ? 'Active on the portal' : 'In progress'}
            right={fmtDay(g.created)}
          />
        ))}
      </PanelState>
    </Panel>
  );
}

// Same query (and params) as the Documents page's first load, so opening that
// page after this one is served from cache.
function DocumentsPanel() {
  const { data, isLoading, isError } = useSearchClientDocumentsQuery({ sort: 'recent' });
  const docs = useMemo(() => data?.documents || [], [data]);

  const stats = useMemo(() => {
    const now = Date.now();
    const within = (days) => docs.filter(d => d.uploaded_at && now - new Date(d.uploaded_at).getTime() <= days * DAY_MS).length;
    return [
      { label: 'Documents', value: data?.total ?? docs.length },
      { label: 'Last 7 days', value: within(7) },
      { label: 'Last 30 days', value: within(30) },
    ];
  }, [docs, data]);

  return (
    <Panel icon="folder2-open" title="Documents" to="/work/documents">
      <PanelState isLoading={isLoading} isError={isError} empty={!docs.length && 'No documents in your offices yet.'}>
        <Stats items={stats} />
        <ListHeading>Latest</ListHeading>
        {docs.slice(0, 5).map(d => (
          <Row
            key={d.upload_id || d.doc_id}
            primary={d.display_label || d.doc_label || d.original_filename}
            secondary={d.client_name}
            right={fmtDay(d.uploaded_at)}
          />
        ))}
      </PanelState>
    </Panel>
  );
}

// The CRM page's own tiles endpoint with no filters, i.e. the page's default
// view (clients, duplicates folded together).
function CrmPanel() {
  const { data, isLoading, isError } = useGetCrmSummaryQuery({});
  const sources = data?.by_source || [];

  return (
    <Panel icon="person-lines-fill" title="CRM" to="/work/crm">
      <PanelState isLoading={isLoading} isError={isError} empty={data && !data.total && 'No CRM clients in your offices yet.'}>
        <Stats items={[
          { label: 'Clients', value: data?.total },
          { label: 'Households', value: data?.households },
          { label: 'In Salesforce', value: data?.linked_fsc, color: work.green },
        ]} />
        {data?.possible_duplicates > 0 && (
          <Link to="/work/crm" style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.8rem',
            background: 'rgba(208,155,44,0.12)', borderRadius: 4, padding: '0.45rem 0.6rem',
            color: work.ink, fontSize: '0.82rem', textDecoration: 'none',
          }}>
            <i className="bi bi-exclamation-triangle" style={{ color: work.goldenrod }} />
            {fmtNum(data.possible_duplicates)} possible duplicate{data.possible_duplicates === 1 ? '' : 's'} to review
          </Link>
        )}
        <ListHeading>By firm</ListHeading>
        {sources.slice(0, 5).map(s => (
          <Row key={s.label} primary={s.name || s.label} right={`${fmtNum(s.n)} clients`} />
        ))}
      </PanelState>
    </Panel>
  );
}

function BetaPanel({ pages, isMobile }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 0 0.5rem' }}>
        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: work.muted, textTransform: 'uppercase', letterSpacing: '1px' }}>
          In beta
        </span>
        <span style={{ fontSize: '0.75rem', color: work.muted }}>· still being built, shown to the beta group only</span>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(240px, 1fr))',
        gap: '0.75rem',
      }}>
        {pages.map(p => (
          <Link key={p.path} to={p.path} style={{
            background: work.surface, border: `1px dashed ${work.hairlineStrong}`, borderRadius: 6,
            padding: '0.8rem 0.9rem', display: 'flex', alignItems: 'center', gap: '0.7rem',
            textDecoration: 'none', color: work.ink,
          }}>
            <i className={`bi bi-${p.icon}`} style={{ color: work.deepBlue }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{p.label}</div>
              <div style={{ color: work.muted, fontSize: '0.76rem' }}>{p.sub}</div>
            </div>
            <span style={{
              background: 'rgba(208,155,44,0.18)', color: work.goldenrod, fontSize: '0.6rem', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.5px', padding: '0.15rem 0.45rem', borderRadius: 3,
            }}>
              Beta
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
BetaPanel.propTypes = { pages: PropTypes.arrayOf(PropTypes.object).isRequired, isMobile: PropTypes.bool };
